import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface SessionContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  checkSession: () => Promise<boolean>;
  login: (token: string) => void;
  logout: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize authentication state based on existing token
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return !!localStorage.getItem('token');
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const verificationInProgress = useRef(false);
  const lastVerificationTime = useRef(0);

  const checkSession = useCallback(async () => {
    // Prevent multiple simultaneous verification calls
    if (verificationInProgress.current) {
      return isAuthenticated;
    }

    // Debounce verification calls (minimum 5 seconds between calls to reduce API load)
    const now = Date.now();
    if (now - lastVerificationTime.current < 5000) {
      return isAuthenticated;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return false;
    }

    try {
      verificationInProgress.current = true;
      lastVerificationTime.current = now;
      
      // Add token to request headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Make a request to verify the token
      await axios.get('/api/auth/verify');
      setIsAuthenticated(true);
      setIsLoading(false);
      return true;
    } catch (error) {
      // If the token is invalid or expired
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setIsAuthenticated(false);
      setIsLoading(false);
      return false;
    } finally {
      verificationInProgress.current = false;
    }
  }, [isAuthenticated]);

  const login = useCallback((token: string) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setIsAuthenticated(true);
    setIsLoading(false);
    console.log('SessionContext: User authenticated successfully');
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setIsLoading(false);
    navigate('/login');
  }, [navigate]);

  // Check session on mount and when the token changes
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      checkSession();
    } else {
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, []); // Remove checkSession from dependencies to prevent loops

  // Add axios interceptor to handle 401 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  // Check session on page visibility change (but debounced)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        // Only check if user was already authenticated and came back to the tab
        checkSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkSession, isAuthenticated]);

  const value = {
    isAuthenticated,
    isLoading,
    checkSession,
    login,
    logout
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}; 